import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch
} from "./UI";
import { Check } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";

export function ProjectsSection() {
  const { addAlert } = useAlert();
  const [settings, setSettings] = useState({
    measurementUnit: "sqft",
    currency: "INR",
    approvalWorkflow: true,
    pettyCashThreshold: "10000",
    expenseApproval: "5000",
    autoGenerateIds: true,
  });

  const handleSave = () => {
    addAlert("Project defaults updated", "success");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Project Defaults</h2>
        <p className="text-text-gray mt-1">Configure default settings for new projects.</p>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Measurement & Currency</CardTitle>
          <CardDescription>Set your preferred units for project calculations.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Area Unit</Label>
              <Select value={settings.measurementUnit} onValueChange={(v) => setSettings({ ...settings, measurementUnit: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sqft">Square Feet (sq ft)</SelectItem>
                  <SelectItem value="sqm">Square Meters (sq m)</SelectItem>
                  <SelectItem value="sqyd">Square Yards (sq yd)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={settings.currency} onValueChange={(v) => setSettings({ ...settings, currency: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">Indian Rupee (₹)</SelectItem>
                  <SelectItem value="USD">US Dollar ($)</SelectItem>
                  <SelectItem value="AED">UAE Dirham (د.إ)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Approval Workflows</CardTitle>
          <CardDescription>Control how approvals work across projects.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-bg/30">
            <div>
              <p className="font-medium text-primary-text">Enable Approval Workflow</p>
              <p className="text-sm text-text-gray">Require approvals for material requests and expenses.</p>
            </div>
            <Switch
              checked={settings.approvalWorkflow}
              onCheckedChange={(v) => setSettings({ ...settings, approvalWorkflow: v })}
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-bg/30">
            <div>
              <p className="font-medium text-primary-text">Auto-generate Project IDs</p>
              <p className="text-sm text-text-gray">Automatically create sequential project codes.</p>
            </div>
            <Switch
              checked={settings.autoGenerateIds}
              onCheckedChange={(v) => setSettings({ ...settings, autoGenerateIds: v })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Financial Thresholds</CardTitle>
          <CardDescription>Set limits for petty cash and expense approvals.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pettyCash">Petty Cash Limit (₹)</Label>
              <Input
                id="pettyCash"
                type="number"
                value={settings.pettyCashThreshold}
                onChange={(e) => setSettings({ ...settings, pettyCashThreshold: e.target.value })}
              />
              <p className="text-xs text-text-gray">Maximum amount before requiring approval.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expenseApproval">Auto-approve Below (₹)</Label>
              <Input
                id="expenseApproval"
                type="number"
                value={settings.expenseApproval}
                onChange={(e) => setSettings({ ...settings, expenseApproval: e.target.value })}
              />
              <p className="text-xs text-text-gray">Expenses below this are auto-approved.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Check className="h-4 w-4" />
          Save Changes
        </Button>
      </div>
    </div>
  );
}
